import classNames from 'classnames';
import { connect, getIn } from 'formik';
import React, { useMemo, useState } from 'react';
import { AlertTriangle, Check, Info } from 'react-feather';
import Select from 'react-select';
import Switch from "react-switch";
import Textarea from 'react-textarea-autosize';
import { UncontrolledTooltip } from 'reactstrap';
import { EVO } from '../EVO';
import { findOptions, getOptionValue } from './FloatingPlaceholder';
import './PTElements.css';

export const PTInputText = ({ field, info, children, type, step, autoFocus, maxlength, min, max, innerRef, form: { errors, setFieldValue }, ...props }) => {
    const otherProps = {
        autoFocus: autoFocus,
        maxLength: maxlength,
        min: min,
        max: max
    };

    return <div className={classNames(`pt-input pt-input-text pt-input-${field.name.toLowerCase()}`, {
        'inline': props.inline,
        'errored': errors && errors[field.name],
        'light': props.light,
        'small': props.small,
        'left': props.left,
        'full': props.full
    })}>
        <div className={classNames('pt-input-inner')}>
            <input className="pt-input-item"
                type={type || "text"} {...field}
                onChange={e => {
                    field.onChange(e);
                    if (props.onChange) {
                        props.onChange(e.target.value, e.target);
                    }
                }}
                ref={innerRef}
                required={props.required}
                min={props.min}
                placeholder={props.placeholder}
                step={type && type === 'number' ? (step || 1) : undefined}
                id={field.name} {...otherProps} />
            {info && <span className="floating-info" data-tip={info}><Info color="black" /></span>}
            {children}
        </div>
    </div>
};

export const PTInputSelect = ({ options, onAdd, onRemove, onChange, field, form, isMulti, removeSearchOnSelect, form: { errors }, ...props }) => {
    const [search, setSearch] = useState('');

    const onInputChange = (value, e) => {
        if (e.action === 'input-change') {
            setSearch(value);
        }
    }

    return (
        <div className={classNames(`pt-input pt-input-select pt-input-${field.name.toLowerCase()}`, {
            'errored': errors[field.name],
            'pt-input-select-multi': isMulti,
            'inline': props.inline,
            'left': props.left
        })}>
            <div className={classNames('floating-label')}>
                <Select
                    options={options}
                    name={field.name}
                    value={findOptions(options, field)}
                    onChange={(option, e, a) => {
                        var newOpts = getOptionValue(option);
                        if (isMulti) {
                            if (e.action === 'select-option') {
                                if (onAdd && !onAdd(e.option)) {

                                }
                            }
                            else if (e.action === 'remove-value') {
                                if (onRemove && !onRemove(e.removedValue)) {
                                    return false;
                                }
                            }
                        }

                        if (onChange) {
                            onChange(option, e, a);
                        }

                        if (!isMulti || removeSearchOnSelect) {
                            setSearch('');
                        }

                        form.setFieldValue(field.name, newOpts);
                    }}
                    onBlur={field.onBlur}
                    isMulti={isMulti}
                    isClearable={field.isClearable}
                    classNamePrefix="pt-input-select"
                    id={field.name}
                    onInputChange={onInputChange}
                    inputValue={search}
                    isSearchable={true}
                    menuIsOpen={isMulti}
                    placeholder="Click to Select..."
                />
            </div>
        </div>
    );
};

export const PTInputSelectBasic = ({ options, field, setFieldValue, isMulti, errors, ...props }) => (
    <div className={classNames(`pt-input pt-input-select pt-input-${field.name.toLowerCase()}`, {
        'errored': errors && errors[field.name],
        'pt-input-select-multi': isMulti,
        'inline': props.inline,
        'left': props.left
    })}>
        <div className={classNames('floating-label')}>
            <Select
                options={options}
                name={field.name}
                value={findOptions(options, field)}
                onChange={(option) => setFieldValue(field.name, getOptionValue(option))}
                isMulti={isMulti}
                classNamePrefix="pt-input-select"
                id={field.name}
                isSearchable={true}
                menuIsOpen={isMulti}
                placeholder="Select..."
                {...props}
            />
        </div>
    </div>
);

export const PTInputTextArea = ({ field, onClick, form: { errors, setFieldValue }, ...props }) => (
    <div className={classNames(`pt-input pt-input-text pt-input-${field.name.toLowerCase()}`, {
        'center': props.center,
        'errored': errors[field.name],
        'left': props.left
    })}>
        <div className={classNames('floating-label')}>
            <Textarea className="pt-input-item" {...field} id={field.name} onClick={onClick}
                onChange={props.onChange ? props.onChange : (e) => { setFieldValue(field.name, e.target.value) }}
            />
            <label htmlFor={field.name}>{props.placeholder}</label>
        </div>
    </div>
);

export const PTInputCheckbox = ({ field, placeholder, info, form: { errors }, ...props }) => (
    <label className={classNames(`pt-input pt-input-checkbox pt-input-${field.name.toLowerCase()}`, {
        'center': props.center,
        'errored': errors[field.name],
        'left': props.left
    })}>
        <span className={classNames('checkbox', { 'checked': field.value })}>{field.value && <Check color="#0e0f42" />}</span>
        <input {...field} type="checkbox" checked={field.value} />
        <span className="label">{placeholder}</span>
        {info && <span className="pt-input-checkbox-info" data-tip={info}><Info color="black" /></span>}
    </label>
);

export const PTInputCheckboxBasic = ({ field, placeholder, info, errors, ...props }) => {
    const [checked, setChecked] = useState(field.value);
    const onChange = e => {
        setChecked(e.target.checked);
        if (props.onChange) {
            props.onChange({ value: e.target.checked });
        }
    };

    return <label className={classNames(`pt-input pt-input-checkbox pt-input-${field.name.toLowerCase()}`, {
        'center': props.center,
        'errored': errors && errors[field.name],
        'left': props.left,
        'flex': props.flex
    })}>
        <span className={classNames('checkbox', { 'checked': checked })}>{checked && <Check color="#0e0f42" />}</span>
        <input type="checkbox" checked={checked} onChange={e => onChange(e)} />
        <span className="label">{placeholder}</span>
        {info && <span className="pt-input-checkbox-info" data-tip={info}><Info color="black" /></span>}
    </label>
};

export const PTSwitch = props => {
    return <Switch 
        height={40}
        width={80}
        handleDiameter={30}
        onColor="#62D392"
        offColor="#EE3337"
        checkedIcon={<span className="chore-on chore-toggle">ON</span>}
        uncheckedIcon={<span className="chore-off chore-toggle">OFF</span>}
        {...props}
    />
};

export const PTInfo = ({ info, colour }) => {
    const id = `info-${EVO.guid().split('-')[0]}`;
    return !info ? null : useMemo(() => <span>
        <span id={id} className="pt-input-info" data-background-color="#0e0f42" data-effect="solid" data-clickable={true}><Info color={colour || "black"} /></span>
        <UncontrolledTooltip placement="right" autohide={false} target={id}>{info}</UncontrolledTooltip>
    </span>, [info]);
};

export const PTE = ({ fields, ...props }) => {
    if (!EVO.any(fields))
        return null;

    const errors = fields.map(f => {
        return {
            error: getIn(props.formik.errors, f),
            field: f
        };
    }).filter(f => f.error);

    if (!EVO.any(errors))
        return null;

    return errors.map(e => <div key={e.field} className="pt-input-error"><AlertTriangle color="#e80c4d" />{e.error}</div>);
};

export const PTErrorField = ({ error }) => <div className="pt-input-error"><AlertTriangle color="#e80c4d" />{error}</div>

export const PTErrors = connect(PTE);


// WEBPACK FOOTER //
// ./src/components/PTElements.js